import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown,
  Upload, Download, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ExpectedChargeItemsTab } from './ExpectedChargeItemsTab';
import { NameAliasesTab } from './NameAliasesTab';
import { UnmappedItemsTab } from './UnmappedItemsTab';
import {
  chargeMapAdminService,
  CANONICAL_SECTIONS,
  MAP_STATES,
  DEMAND_UNITS,
  type ChargeMapRow,
  type ChargeMapSortKey,
  type ChargeMapFacets,
  type ExpectedChargeItemRow,
  type BulkUploadResponse,
} from '../../services/chargeMapAdminService';

const PAGE_SIZE = 25;

const SELECT_CLASS =
  'h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const FORM_SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const EMPTY_FORM: Partial<ChargeMapRow> = {
  retailer_id: '',
  distributor: '',
  tariff_code: '',
  source_tariff: '',
  bill_section: '',
  bill_section_canonical: 'retail',
  bill_sub_section: '',
  bill_item: '',
  expected_item: '',
  expected_section: 'retail',
  demand_unit: null,
  group_key: '',
  map_state: 'needs_review',
  notes: '',
  source_state: '',
};

/**
 * `needs_review` means nobody has decided that mapping yet — 145 rows of the
 * source workbook are in that state, and finding them is a primary reason an
 * admin opens this screen. It is the only badge with a border and a warning
 * icon, so a page of rows reads at a glance.
 */
const MAP_STATE_COLOURS: Record<string, string> = {
  validated: 'bg-green-100 text-green-800',
  passthrough: 'bg-blue-100 text-blue-800',
  unvalidated: 'bg-gray-100 text-gray-600',
  needs_review: 'bg-amber-100 text-amber-900 ring-1 ring-amber-500 font-semibold',
  group_member: 'bg-purple-100 text-purple-800',
};

const MAP_STATE_LABELS: Record<string, string> = {
  validated: 'Validated',
  passthrough: 'Passthrough',
  unvalidated: 'Unvalidated',
  needs_review: 'Needs review',
  group_member: 'Group member',
};

const SECTION_OPTIONS = CANONICAL_SECTIONS.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));

/** The grid's columns in order; `sort` is set for the ones the route can sort. */
const COLUMNS: Array<{ label: string; sort?: ChargeMapSortKey }> = [
  { label: 'Retailer', sort: 'retailer' },
  { label: 'Distributor', sort: 'distributor' },
  { label: 'Tariff', sort: 'tariff' },
  { label: 'Bill Section', sort: 'bill_section' },
  { label: 'Bill Item', sort: 'bill_item' },
  { label: 'Expected Item', sort: 'expected_item' },
  { label: 'Section', sort: 'expected_section' },
  { label: 'Unit', sort: 'demand_unit' },
  { label: 'Map State', sort: 'map_state' },
];
// Checkbox + data columns + actions.
const COL_SPAN = COLUMNS.length + 2;

/** Mirrors BULK_DELETE_MAX in chargeMapRoutes.ts. */
const BULK_DELETE_MAX = 500;

const CHECKBOX_CLASS = 'h-3.5 w-3.5 rounded-[4px] border-gray-300 cursor-pointer';

const MapStateBadge: React.FC<{ state: string }> = ({ state }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${MAP_STATE_COLOURS[state] ?? 'bg-gray-100 text-gray-600'}`}
  >
    {state === 'needs_review' && <AlertTriangle className="h-3 w-3" />}
    {MAP_STATE_LABELS[state] ?? state}
  </span>
);

export const ChargeMapManagement: React.FC = () => {
  const [tab, setTab] = useState('mappings');

  const [rows, setRows] = useState<ChargeMapRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [retailerFilter, setRetailerFilter] = useState('');
  const [distributorFilter, setDistributorFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [sort, setSort] = useState<ChargeMapSortKey>('retailer');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Ticked rows, kept across pages so a delete can span several.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);

  const [facets, setFacets] = useState<ChargeMapFacets | null>(null);
  const [vocabulary, setVocabulary] = useState<ExpectedChargeItemRow[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ChargeMapRow | null>(null);
  const [form, setForm] = useState<Partial<ChargeMapRow>>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<ChargeMapRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);

  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const retailers = facets?.retailers ?? [];
  const distributors = facets?.distributors ?? [];
  const mapStates = facets?.map_states ?? [...MAP_STATES];

  // Filter options. Refetched after a write, so a brand-new distributor or a
  // retailer's first mapping shows up in the dropdowns.
  useEffect(() => {
    let active = true;
    chargeMapAdminService.facets().then((res) => {
      if (!active) return;
      if (res.success) setFacets(res.data ?? null);
      else setError(res.error ?? 'Failed to load filters');
    });
    return () => { active = false; };
  }, [reloadToken]);

  // The `expected_item` select's options. Reloaded when the mappings tab is
  // re-entered, so an item just added on the vocabulary tab is selectable.
  useEffect(() => {
    if (tab !== 'mappings') return;
    let active = true;
    chargeMapAdminService.vocabulary.list().then((res) => {
      if (!active) return;
      if (res.success) setVocabulary(res.data ?? []);
      else setError(res.error ?? 'Failed to load the expected-item vocabulary');
    });
    return () => { active = false; };
  }, [tab, reloadToken]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [retailerFilter, distributorFilter, stateFilter, sort, sortDir]);

  // A filter change makes the old ticks invisible; deleting rows you can no
  // longer see is a trap, so drop them.
  useEffect(() => { setSelected(new Set()); }, [search, retailerFilter, distributorFilter, stateFilter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    chargeMapAdminService.list({
      search,
      retailer: retailerFilter,
      distributor: distributorFilter,
      map_state: stateFilter,
      sort,
      dir: sortDir,
      page,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (!active) return;
        if (!res.success) { setError(res.error ?? 'Failed to load'); setRows([]); setCount(0); }
        else { setRows(res.data ?? []); setCount(res.count ?? 0); }
        setLoading(false);
      });
    return () => { active = false; };
  }, [search, retailerFilter, distributorFilter, stateFilter, sort, sortDir, page, reloadToken]);

  const refresh = () => setReloadToken((t) => t + 1);

  const toggleSort = (key: ChargeMapSortKey) => {
    if (sort === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setSortDir('asc'); }
  };

  const pageIds = rows.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const somePageSelected = pageIds.some((id) => selected.has(id));

  const toggleRow = (id: string) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // A fully ticked page clears only its own rows; otherwise the page fills up.
  const togglePage = () => setSelected((cur) => {
    const next = new Set(cur);
    if (allPageSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    return next;
  });

  const handleBulkDelete = async () => {
    setBulkDeleteBusy(true);
    const res = await chargeMapAdminService.removeMany([...selected]);
    setBulkDeleteBusy(false);
    setBulkDeleteOpen(false);
    if (!res.success) { setError(res.error ?? 'Delete failed'); return; }
    const asked = selected.size;
    if ((res.deleted ?? 0) < asked) {
      setError(`Deleted ${res.deleted ?? 0} of ${asked} selected rows — the rest were already gone.`);
    }
    setSelected(new Set());
    setPage(1);
    refresh();
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: ChargeMapRow) => {
    setEditing(row);
    setForm({
      retailer_id: row.retailer_id,
      distributor: row.distributor ?? '',
      tariff_code: row.tariff_code ?? '',
      source_tariff: row.source_tariff ?? '',
      bill_section: row.bill_section,
      bill_section_canonical: row.bill_section_canonical,
      bill_sub_section: row.bill_sub_section ?? '',
      bill_item: row.bill_item,
      expected_item: row.expected_item ?? '',
      expected_section: row.expected_section,
      demand_unit: row.demand_unit,
      group_key: row.group_key ?? '',
      map_state: row.map_state,
      notes: row.notes ?? '',
      source_state: row.source_state ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.retailer_id) { setFormError('Retailer is required'); return; }
    if (!form.bill_section?.trim()) { setFormError('Bill section is required'); return; }
    if (!form.bill_item?.trim()) { setFormError('Bill item is required'); return; }
    if (!form.bill_section_canonical) { setFormError('Canonical section is required'); return; }
    if (!form.expected_section) { setFormError('Expected section is required'); return; }
    if (!form.map_state) { setFormError('Map state is required'); return; }

    // The generated key columns are deliberately absent: Postgres rejects a
    // statement that names them, and they derive from the fields below anyway.
    const body: Partial<ChargeMapRow> = {
      retailer_id: form.retailer_id,
      distributor: form.distributor?.trim() || null,
      tariff_code: form.tariff_code?.trim() || null,
      source_tariff: form.source_tariff?.trim() || null,
      bill_section: form.bill_section.trim(),
      bill_section_canonical: form.bill_section_canonical,
      bill_sub_section: form.bill_sub_section?.trim() || null,
      bill_item: form.bill_item.trim(),
      expected_item: form.expected_item || null,
      expected_section: form.expected_section,
      demand_unit: form.demand_unit ?? null,
      group_key: form.group_key?.trim() || null,
      map_state: form.map_state,
      notes: form.notes?.trim() || null,
      source_state: form.source_state?.trim() || null,
    };

    setSaving(true);
    setFormError(null);
    const res = editing
      ? await chargeMapAdminService.update(editing.id, body)
      : await chargeMapAdminService.create(body);
    setSaving(false);

    // The route answers a readable sentence for an unknown expected item or a
    // duplicate conflict key; show it rather than a generic failure.
    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    refresh();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    const res = await chargeMapAdminService.remove(deleting.id);
    setDeleteBusy(false);
    if (!res.success) { setError(res.error ?? 'Delete failed'); setDeleting(null); return; }
    setSelected((cur) => { const next = new Set(cur); next.delete(deleting.id); return next; });
    setDeleting(null);
    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    else refresh();
  };

  const handleExport = async () => {
    setExporting(true);
    const res = await chargeMapAdminService.exportCsv({
      retailer: retailerFilter,
      distributor: distributorFilter,
    });
    setExporting(false);
    if (!res.success) setError(res.error ?? 'Export failed');
  };

  const openBulkDialog = () => { setBulkFile(null); setBulkResult(null); setBulkDialogOpen(true); };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkFile(e.target.files?.[0] ?? null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    const result = await chargeMapAdminService.bulkUpload(bulkFile);
    setBulkResult(result);
    setBulkUploading(false);
    if (result.success && (result.upserted ?? 0) > 0) refresh();
  };

  const field = (key: keyof ChargeMapRow) => String(form[key] ?? '');
  const selectedItemUnit = vocabulary.find((v) => v.name === form.expected_item)?.unit ?? null;
  const setField = (key: keyof ChargeMapRow, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const bulkErrors = bulkResult?.errors ?? [];

  return (
    <Tabs value={tab} onValueChange={setTab} className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Retailer Charge Map</h1>
        <p className="text-sm text-muted-foreground">
          Maps each retailer&apos;s own bill wording to an expected charge item, so a bill line can be recognised and
          validated. Rows marked <span className="font-medium text-amber-900">needs review</span> have not been decided yet.
        </p>
      </div>

      <TabsList>
        <TabsTrigger value="mappings">Mappings</TabsTrigger>
        <TabsTrigger value="vocabulary">Expected Charge Items</TabsTrigger>
        <TabsTrigger value="names">Approved Names</TabsTrigger>
        <TabsTrigger value="unmapped">Unmapped Items</TabsTrigger>
      </TabsList>

      <TabsContent value="mappings" className="space-y-6">
        {/* Filters + toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[200px] flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search bill item, expected item, section…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8"
            />
          </div>

          <select
            value={retailerFilter}
            onChange={(e) => setRetailerFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All retailers</option>
            {retailers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>

          <select
            value={distributorFilter}
            onChange={(e) => setDistributorFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All distributors</option>
            {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All map states</option>
            {mapStates.map((s) => <option key={s} value={s}>{MAP_STATE_LABELS[s] ?? s}</option>)}
          </select>

          {/* One click to the rows nobody has decided yet. */}
          <Button
            variant={stateFilter === 'needs_review' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStateFilter(stateFilter === 'needs_review' ? '' : 'needs_review')}
            className="gap-1.5"
          >
            <AlertTriangle className="h-4 w-4" /> Needs review
          </Button>

          {/* The charges taken as billed and added back on the internal screens
              (never on the client report). Narrow by retailer or distributor to
              see them per tariff. */}
          <Button
            variant={stateFilter === 'passthrough' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStateFilter(stateFilter === 'passthrough' ? '' : 'passthrough')}
            className="gap-1.5"
            title="Charges set to Passthrough: taken as billed, left out of the variance and added back internally"
          >
            Pass-through
          </Button>

          <span className="text-sm text-muted-foreground">{count.toLocaleString()} rows</span>
          {selected.size > 0 && (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteOpen(true)}
                disabled={selected.size > BULK_DELETE_MAX}
                title={selected.size > BULK_DELETE_MAX ? `At most ${BULK_DELETE_MAX} rows per delete` : undefined}
                className="gap-1.5"
              >
                <Trash2 className="h-4 w-4" /> Delete {selected.size} selected
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
            </>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => void handleExport()} disabled={exporting} className="gap-1.5">
            <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button variant="outline" onClick={openBulkDialog} className="gap-1.5">
            <Upload className="h-4 w-4" /> Bulk upload
          </Button>
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" /> Add mapping
          </Button>
        </div>

        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      title="Select all on this page"
                      className={CHECKBOX_CLASS}
                      checked={allPageSelected}
                      disabled={loading || rows.length === 0}
                      ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                      onChange={togglePage}
                    />
                  </TableHead>
                  {COLUMNS.map((c) => (
                    <TableHead key={c.label}>
                      {c.sort ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(c.sort!)}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                          aria-sort={sort === c.sort ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                        >
                          {c.label}
                          {sort !== c.sort ? <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            : sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-blue-600" />
                              : <ChevronDown className="h-3 w-3 text-blue-600" />}
                        </button>
                      ) : c.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={COL_SPAN} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={COL_SPAN} className="text-center text-muted-foreground py-8">No rows found</TableCell></TableRow>
                ) : rows.map((row) => (
                  <TableRow key={row.id} data-state={selected.has(row.id) ? 'selected' : undefined}>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.bill_item}`}
                        className={CHECKBOX_CLASS}
                        checked={selected.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{row.retailer_name ?? '—'}</TableCell>
                    <TableCell>{row.distributor ?? '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{row.tariff_code ?? '—'}</TableCell>
                    <TableCell className="max-w-[160px] truncate text-sm" title={row.bill_section}>{row.bill_section}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={row.bill_item}>{row.bill_item}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-sm" title={row.expected_item ?? ''}>
                      {row.expected_item ?? '—'}
                    </TableCell>
                    <TableCell><Badge variant="outline">{row.expected_section}</Badge></TableCell>
                    <TableCell className="text-sm">{row.demand_unit ?? '—'}</TableCell>
                    <TableCell><MapStateBadge state={row.map_state} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(row)} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="vocabulary">
        <ExpectedChargeItemsTab
          onShowMappings={(name) => {
            setRetailerFilter('');
            setDistributorFilter('');
            setStateFilter('');
            setSearchInput(name);
            setTab('mappings');
          }}
        />
      </TabsContent>

      <TabsContent value="names">
        <NameAliasesTab />
      </TabsContent>

      <TabsContent value="unmapped">
        <UnmappedItemsTab
          onMap={(item) => {
            // Pre-fill what the invoice told us; the admin picks the target.
            const section = (CANONICAL_SECTIONS as readonly string[]).includes(item.section)
              ? (item.section as ChargeMapRow['bill_section_canonical'])
              : 'other';
            setEditing(null);
            setForm({
              ...EMPTY_FORM,
              retailer_id: item.retailer_id ?? '',
              distributor: item.distributors.length === 1 ? item.distributors[0] : '',
              bill_section: item.section,
              bill_section_canonical: section,
              expected_section: section,
              bill_item: item.bill_item,
            });
            setFormError(null);
            setDialogOpen(true);
          }}
        />
      </TabsContent>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!bulkUploading) setBulkDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload Charge Mappings</DialogTitle>
            <DialogDescription>
              Upload either the multi-sheet source workbook (one sheet per retailer, headers on row 2) or a flat
              CSV/XLSX matching the <strong>Export CSV</strong> columns — re-uploading an export is idempotent. An
              unknown retailer or expected item is reported as a row error; the rest of the file still loads.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => bulkFileRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              {bulkFile ? (
                <span className="text-sm font-medium">{bulkFile.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Click to select CSV or XLSX file</span>
              )}
              <input ref={bulkFileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleBulkFileChange} />
            </div>

            {bulkResult && (
              <div className="space-y-2">
                {bulkResult.success ? (
                  <Alert>
                    <AlertDescription>
                      <span className="font-medium">Upload complete</span>
                      <span className="text-sm ml-2">
                        {bulkResult.upserted} written · {bulkResult.duplicates_collapsed ?? 0} duplicates collapsed ·{' '}
                        {bulkResult.skipped ?? 0} skipped (of {bulkResult.total} rows, read as {bulkResult.shape})
                      </span>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive"><AlertDescription>{bulkResult.error}</AlertDescription></Alert>
                )}
                {bulkErrors.length > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs space-y-1">
                    {bulkErrors.map((e, i) => (
                      // The sheet name is not decoration: "row 47" is ambiguous
                      // across a 16-sheet workbook. `row: 0` is a sheet-level problem.
                      <div key={`${e.sheet ?? ''}-${e.row}-${i}`} className="text-red-600">
                        {e.sheet ? <span className="font-medium">{e.sheet}</span> : null}
                        {e.sheet && e.row > 0 ? ', ' : e.sheet ? ' — ' : ''}
                        {e.row > 0 ? `row ${e.row}: ` : e.sheet ? '' : 'File: '}
                        {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>
              {bulkResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult?.success && (
              <Button onClick={() => void handleBulkUpload()} disabled={!bulkFile || bulkUploading}>
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open && !deleteBusy) setDeleting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete mapping</DialogTitle>
            <DialogDescription>
              {deleting
                ? `Delete “${deleting.bill_item}” under ${deleting.retailer_name ?? 'this retailer'} / ${deleting.bill_section}? This cannot be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleteBusy}>
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirm */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(open) => { if (!bulkDeleteBusy) setBulkDeleteOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {selected.size} mapping{selected.size === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>
              The selected rows (across every page you ticked) will be deleted. Bill lines they matched will show as
              unmapped until mapped again. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleteBusy}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleBulkDelete()} disabled={bulkDeleteBusy}>
              {bulkDeleteBusy ? 'Deleting…' : `Delete ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit mapping' : 'Add mapping'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the fields and save.' : 'Create a new retailer charge mapping.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cm-retailer_id">Retailer <span className="text-red-500">*</span></Label>
              <select
                id="cm-retailer_id"
                value={field('retailer_id')}
                onChange={(e) => setField('retailer_id', e.target.value)}
                className={FORM_SELECT_CLASS}
              >
                <option value="">Select a retailer…</option>
                {retailers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-distributor">Distributor</Label>
              <Input
                id="cm-distributor"
                list="cm-distributor-options"
                placeholder="blank = not network-scoped · MULTI = any"
                value={field('distributor')}
                onChange={(e) => setField('distributor', e.target.value)}
              />
              <datalist id="cm-distributor-options">
                {distributors.map((d) => <option key={d} value={d} />)}
              </datalist>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-tariff_code">Tariff code</Label>
              <Input id="cm-tariff_code" className="font-mono" value={field('tariff_code')} onChange={(e) => setField('tariff_code', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-source_tariff">Source tariff</Label>
              <Input id="cm-source_tariff" className="font-mono" value={field('source_tariff')} onChange={(e) => setField('source_tariff', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-bill_section">Bill section <span className="text-red-500">*</span></Label>
              <Input
                id="cm-bill_section"
                list="cm-bill-section-options"
                placeholder="The retailer's own wording"
                value={field('bill_section')}
                onChange={(e) => setField('bill_section', e.target.value)}
              />
              <datalist id="cm-bill-section-options">
                {(facets?.bill_sections ?? []).map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-bill_section_canonical">Canonical section <span className="text-red-500">*</span></Label>
              <select
                id="cm-bill_section_canonical"
                value={field('bill_section_canonical')}
                onChange={(e) => setField('bill_section_canonical', e.target.value)}
                className={FORM_SELECT_CLASS}
              >
                {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-bill_sub_section">Bill sub-section</Label>
              <Input id="cm-bill_sub_section" value={field('bill_sub_section')} onChange={(e) => setField('bill_sub_section', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-bill_item">Bill item <span className="text-red-500">*</span></Label>
              <Input id="cm-bill_item" value={field('bill_item')} onChange={(e) => setField('bill_item', e.target.value)} />
            </div>

            {/*
              A select, never a free-text box. `expected_item` carries a composite
              foreign key on the exact name, so a typo fails the write; the route
              rejects readably, but the form should not be able to produce one.
            */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cm-expected_item">Expected item</Label>
              <select
                id="cm-expected_item"
                value={field('expected_item')}
                onChange={(e) => {
                  // The item decides the section and, for a demand item, the
                  // unit: "Peak Demand - kVA" is kVA. The server enforces the unit.
                  const item = vocabulary.find((v) => v.name === e.target.value);
                  setForm((f) => ({
                    ...f,
                    expected_item: e.target.value,
                    ...(item ? { expected_section: item.section } : {}),
                    ...(item?.unit ? { demand_unit: item.unit } : {}),
                  }));
                }}
                className={FORM_SELECT_CLASS}
              >
                <option value="">— none (unmapped) —</option>
                {vocabulary.map((v) => (
                  <option key={v.id} value={v.name}>
                    {v.name} ({v.section}){v.validation_possible ? '' : ' · no validation'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                From the Expected Charge Items tab. Add one there first if what you need is missing.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-expected_section">Expected section <span className="text-red-500">*</span></Label>
              <select
                id="cm-expected_section"
                value={field('expected_section')}
                onChange={(e) => setField('expected_section', e.target.value)}
                className={FORM_SELECT_CLASS}
              >
                {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-demand_unit">Demand unit</Label>
              <select
                id="cm-demand_unit"
                value={field('demand_unit')}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  demand_unit: e.target.value === '' ? null : (e.target.value as ChargeMapRow['demand_unit']),
                }))}
                disabled={!!selectedItemUnit}
                title={selectedItemUnit ? 'Set by the expected item' : undefined}
                className={FORM_SELECT_CLASS}
              >
                <option value="">Not a demand charge</option>
                {DEMAND_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-map_state">Map state <span className="text-red-500">*</span></Label>
              <select
                id="cm-map_state"
                value={field('map_state')}
                onChange={(e) => setField('map_state', e.target.value)}
                className={FORM_SELECT_CLASS}
              >
                {MAP_STATES.map((s) => <option key={s} value={s}>{MAP_STATE_LABELS[s]}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-group_key">Group key</Label>
              <Input id="cm-group_key" value={field('group_key')} onChange={(e) => setField('group_key', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-source_state">Source state</Label>
              <Input id="cm-source_state" value={field('source_state')} onChange={(e) => setField('source_state', e.target.value)} />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cm-notes">Notes</Label>
              <Input id="cm-notes" value={field('notes')} onChange={(e) => setField('notes', e.target.value)} />
            </div>
          </div>

          {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};
